import { useMemo, type ReactNode } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, useColorScheme } from '@mui/material/styles';
import { BrandHead } from './BrandHead';
import type { BrandTheme } from './brandTheme';
import { BrandThemeProvider, useBrandTheme } from './BrandThemeContext';
import { buildAppTheme } from './theme';

/** The theme is rebuilt from the painted brand; the CNPWKS-0006 preference picks the scheme. */
function ThemedApp({ children }: { children: ReactNode }) {
  const { brandTheme } = useBrandTheme();
  const theme = useMemo(() => buildAppTheme(brandTheme), [brandTheme]);
  return (
    <ThemeProvider theme={theme} defaultMode="system" modeStorageKey="cnpwks-color-scheme">
      <CssBaseline enableColorScheme />
      <SchemeBrandHead />
      {children}
    </ThemeProvider>
  );
}

/** Reads the resolved scheme (useColorScheme().colorScheme ?? 'light') and renders BrandHead. */
function SchemeBrandHead() {
  const { colorScheme } = useColorScheme();
  return <BrandHead scheme={colorScheme ?? 'light'} />;
}

export function AppThemeProvider({
  children,
  initialBrand,
}: {
  children: ReactNode;
  initialBrand?: BrandTheme | undefined;
}) {
  return (
    <BrandThemeProvider {...(initialBrand ? { initial: initialBrand } : {})}>
      <ThemedApp>{children}</ThemedApp>
    </BrandThemeProvider>
  );
}
